import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useUserRole } from '../hooks/useUserRole';
import { Navigate } from 'react-router-dom';
import ICarrierData from '../types/Carriers';
import CarrierService from '../services/CarrierService';
import FlightRangeService from '../services/FlightRangeService';
import { partnerCarriers, isActivePartner, getDistinctActiveCarriers } from '../utils/carrierUtils';
import './PartnersAdmin.scss';
import AddPartnerModal from '../Components/Modals/AddPartnerModal';
import RemovePartnerModal from '../Components/Modals/RemovePartnerModal';
import PartnerErrorModal from '../Components/Modals/PartnerErrorModal';
import '@aurodesignsystem/auro-toast';

const PartnersAdmin: React.FC = () => {
  const auth = useAuth();
  const { isAdmin } = useUserRole(); // Get the admin status
  const [partners, setPartners] = useState<ICarrierData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddModalOpen, setIsAddModalOpen] = useState<boolean>(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState<boolean>(false);
  const [partnerToDelete, setPartnerToDelete] = useState<ICarrierData | null>(null);
  const [errorModalMessage, setErrorModalMessage] = useState<string>('');
  const [addSuccess, setAddSuccess] = useState<boolean>(false);
  const [removeSuccess, setRemoveSuccess] = useState<boolean>(false);
  const [distinctActiveCarriers, setDistinctActiveCarriers] = useState<Set<string>>(new Set());

  // Use useCallback to memoize the fetchDistinctActiveCarriers function
  const fetchDistinctActiveCarriers = useCallback(async () => {
    try {
      const token = auth.user?.access_token;
      const activeRangesResponse = await FlightRangeService.getActiveFlightRanges(token);
      const activeRanges = Array.isArray(activeRangesResponse.data) ? activeRangesResponse.data : [];

      const distinctCarriers = getDistinctActiveCarriers(activeRanges);
      setDistinctActiveCarriers(distinctCarriers);
    } catch (error) {
      console.error('Error fetching active flight ranges for carrier optimization:', error);
      // Don't set error state here as this is background optimization
    }
  }, [auth]);

  // Use useCallback to memoize the fetchPartners function
  const fetchPartners = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = auth.user?.access_token;
      const response = await CarrierService.getAll(token);

      // Filter out AS and QX carriers
      const partners = partnerCarriers(response.data);

      setPartners(partners);

      // Also fetch distinct active carriers for efficient partner checking
      await fetchDistinctActiveCarriers();
    } catch (error) {
      setError('Failed to load Carriers');
    } finally {
      setIsLoading(false);
    }
  }, [auth, fetchDistinctActiveCarriers]);

  useEffect(() => {
    if (isAdmin) {
      fetchPartners();
    }
  }, [isAdmin, fetchPartners]);

  const addPartner = () => {
    setIsAddModalOpen(true);
  };

  const handleRemovePartner = async (partner: ICarrierData) => {
    try {
      setError(null);

      // If cached distinct carriers is empty, refresh it first
      if (distinctActiveCarriers.size === 0) {
        await fetchDistinctActiveCarriers();
      }

      // Use the cached distinct active carriers for efficient checking
      if (isActivePartner(partner.carrierCode, distinctActiveCarriers)) {
        setErrorModalMessage(`There are active flight ranges linked to ${(partner.carrierName || 'Unknown Partner').trim()}. Modify them before removing this partner.`);
        setIsErrorModalOpen(true);
        return;
      }

      // If not associated with active flight ranges, show confirmation modal
      setPartnerToDelete(partner);
      setIsDeleteModalOpen(true);
    } catch (error) {
      console.error('Error checking partner activity:', error);
      setErrorModalMessage('Unable to verify flight range associations. Please try again.');
      setIsErrorModalOpen(true);
    }
  };

  const handleAddPartner = async (partnerData: { carrierCode: string; carrierName: string }) => {
    try {
      setError(null);
      const token = auth.user?.access_token;
      await CarrierService.create({
        carrierCode: partnerData.carrierCode,
        carrierName: partnerData.carrierName
      }, token);
      setIsAddModalOpen(false);
      fetchPartners();
      setAddSuccess(true);
      setTimeout(() => setAddSuccess(false), 10000);
    } catch (error: any) {
      setIsAddModalOpen(false); // Hide modal immediately on error
      if (error?.response?.status === 400) {
        setError('Failed to add partner - duplicate');
      } else {
        setError('Failed to add partner');
      }
    }
  };

  const deletePartner = async () => {
    if (!partnerToDelete) return;
    
    try {
      setError(null);
      const token = auth.user?.access_token;

      // Ensure we have a valid carrier ID
      if (!partnerToDelete.carrierId && partnerToDelete.carrierId !== 0) {
        throw new Error('Invalid carrier ID');
      }

      // Proceed with deletion (active flight ranges already checked)
      await CarrierService.remove(partnerToDelete.carrierId!, token);

      setIsDeleteModalOpen(false);
      setPartnerToDelete(null);
      fetchPartners(); // This will also refresh the distinct active carriers

      // Show success toast
      setRemoveSuccess(true);
      setTimeout(() => setRemoveSuccess(false), 10000);
    } catch (error: any) {
      setIsDeleteModalOpen(false);

      if (error?.response?.status === 404) {
        setError('Cannot remove partner: partner not found');
      } else if (error?.response?.status === 400) {
        setError(error?.response?.data || 'Cannot remove partner: invalid request');
      } else if (error?.response?.status === 409) {
        setError('Cannot remove partner: partner is in use');
      } else {
        setError('Failed to delete partner');
      }
    }
  };  // Redirect non-admin users
  if (!isAdmin) {
    return <Navigate to="/flight-ranges" replace />;
  }

  return (
    <div className="partners-admin">
      <div className="partners-admin__header">
        <div className="partners-admin__header-right">
          <button 
            className="add-partner-button" 
            onClick={addPartner}
          >
            + Add new partner
          </button>
        </div>
      </div>
      {error && (
        <div className="partners-admin__error" role="alert" aria-live="assertive">
          {error}
        </div>
      )}
      {isLoading ? (
        <div className="partners-admin__loading" role="status" aria-live="polite">
          Loading Carriers...
        </div>
      ) : (
        <div className="partners-admin__table-wrapper">
          <table className="partners-admin__table" role="grid" aria-label="Partner Airlines">
            <thead>
              <tr>
                <th scope="col">CARRIER CODE</th>
                <th scope="col">PARTNER NAME</th>
                <th scope="col" aria-hidden="true"></th>
              </tr>
            </thead>
            <tbody>
              {partners.map((partner) => (
                <tr key={partner.carrierId}>
                  <td>{partner.carrierCode}</td>
                  <td>{partner.carrierName}</td>
                  <td className="action-buttons-right">
                    <button
                      className="flight-range-modify__remove-link"
                      onClick={() => handleRemovePartner(partner)}
                      aria-label={`Remove ${partner.carrierName}`}
                      type="button"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <RemovePartnerModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={deletePartner}
        partnerName={`${partnerToDelete?.carrierName} (${partnerToDelete?.carrierCode})`}
      />
      <AddPartnerModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onConfirm={handleAddPartner}
      />
      <PartnerErrorModal
        isOpen={isErrorModalOpen}
        onClose={() => setIsErrorModalOpen(false)}
        message={errorModalMessage}
      />
      <div className="partners-admin__toast">
        {addSuccess && (
          <auro-toast
            style={{display: 'block', margin: '0.5rem 0'} as React.CSSProperties}
            variant="success"
            timeTilHide={10000}
            visible={true}
          >
            Partner airline successfully added.
          </auro-toast>
        )}
        {removeSuccess && (
          <auro-toast
            style={{display: 'block', margin: '0.5rem 0'} as React.CSSProperties}
            variant="success"
            timeTilHide={10000}
            visible={true}
          >
            Partner airline successfully removed.
          </auro-toast>
        )}
      </div>
    </div>
  );
};

export default PartnersAdmin;